import { useEffect, useState } from 'react';
import { fetchFaqBlockFile } from '../api/client.js';

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileFetchOpts(clientId, projectId, fileUrl) {
  const opts = {};
  if (fileUrl) opts.fileUrl = fileUrl;
  if (projectId != null) opts.projectId = projectId;
  if (clientId != null) opts.clientId = clientId;
  return opts;
}

/**
 * @param {{ block: any; clientId?: number; projectId?: number; variant?: 'thumb' | 'full' | 'card' }} props
 */
export function FaqMediaBlock({ block, clientId, projectId, variant = 'full' }) {
  const fileOpts = fileFetchOpts(clientId, projectId, block.file_url);
  const file = block.file;
  const hasUpload = block.has_file && file?.name;
  const externalUrl = block.external_url || (!hasUpload ? block.content?.url : null);

  if (!hasUpload && externalUrl) {
    return (
      <figure className={variant === 'thumb' ? 'faq-media-thumb' : 'faq-image'}>
        <img src={externalUrl} alt={block.content?.alt || file?.name || 'Image'} />
        {variant !== 'thumb' && file?.name ? <figcaption className="muted small">{file.name}</figcaption> : null}
      </figure>
    );
  }

  if (!hasUpload) {
    return variant === 'thumb' ? <span className="muted small">No file</span> : null;
  }

  if (file.is_image) {
    return <FaqImagePreview blockId={block.id} fileOpts={fileOpts} file={file} variant={variant} />;
  }

  return <FaqFileCard blockId={block.id} fileOpts={fileOpts} file={file} variant={variant} />;
}

/**
 * @param {{ block: any; clientId?: number; projectId?: number }} props
 */
export function FaqBlockListPreview({ block, clientId, projectId }) {
  if (block.type === 'image' || (block.type === 'file' && block.has_file)) {
    return <FaqMediaBlock block={block} clientId={clientId} projectId={projectId} variant="thumb" />;
  }
  return null;
}

/**
 * @param {{ blockId: number; fileOpts: Record<string, number>; file: any; variant: string }} props
 */
function FaqImagePreview({ blockId, fileOpts, file, variant }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    fetchFaqBlockFile(blockId, fileOpts)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setErr(e.message);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [blockId, fileOpts.clientId, fileOpts.projectId, fileOpts.fileUrl]);

  if (err) {
    return <p className="err small">{err}</p>;
  }
  if (!src) {
    return <p className="muted small">Loading…</p>;
  }

  const name = file.name || 'Image';

  if (variant === 'thumb') {
    return (
      <div className="faq-media-thumb">
        <img src={src} alt={name} />
        <span className="faq-media-name" title={name}>
          {name}
        </span>
      </div>
    );
  }

  return (
    <figure className="faq-image">
      <img src={src} alt={name} />
      <figcaption className="faq-media-caption">
        <span>{name}</span>
        {file.size ? <span className="muted small">{formatFileSize(file.size)}</span> : null}
      </figcaption>
    </figure>
  );
}

/**
 * @param {{ blockId: number; fileOpts: Record<string, number>; file: any; variant: string }} props
 */
function FaqFileCard({ blockId, fileOpts, file, variant }) {
  const [downloading, setDownloading] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(null);

  const name = file.name || block.content?.label || 'File';
  const sizeLabel = formatFileSize(file.size);

  useEffect(() => {
    if (!file.is_image || variant === 'card') {
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    fetchFaqBlockFile(blockId, fileOpts)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setThumbSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [blockId, fileOpts.clientId, fileOpts.projectId, fileOpts.fileUrl, file.is_image, variant]);

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await fetchFaqBlockFile(blockId, fileOpts);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (variant === 'thumb') {
    return (
      <div className="faq-media-thumb faq-media-thumb--file">
        <span className="faq-file-icon" aria-hidden>
          📄
        </span>
        <span className="faq-media-name" title={name}>
          {name}
        </span>
      </div>
    );
  }

  return (
    <div className="faq-file-card">
      {thumbSrc ? (
        <img src={thumbSrc} alt="" className="faq-file-card-thumb" />
      ) : (
        <span className="faq-file-icon faq-file-icon--large" aria-hidden>
          📄
        </span>
      )}
      <div className="faq-file-card-meta">
        <strong className="faq-file-card-name">{name}</strong>
        {sizeLabel ? <span className="muted small">{sizeLabel}</span> : null}
        <button type="button" className="btn-secondary btn-sm" disabled={downloading} onClick={() => void download()}>
          {downloading ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  );
}
